import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, Search, RefreshCw } from 'lucide-react'
import { getProjectPriorArt, reEvaluateProjectPriorArt, getReprocessStatus } from '../../services/api'
import type { ReprocessJob } from '../../types'
import { linkPatentIds } from '../../utils/patentUtils'
import ScoreDisplay from '../shared/ScoreDisplay'

/** Bug #6677: "36 docs analysed" beside a page that says 54 documents read as a contradiction.
 *  Say how many of the invention's documents were analysed when the total is known. */
export function analysedLabel(analysed: number, total?: number): string {
  if (total !== undefined && total >= analysed && total > 0) {
    return `${analysed} of ${total} document${total === 1 ? '' : 's'} analysed`
  }
  return `${analysed} document${analysed === 1 ? '' : 's'} analysed`
}

interface ProjectPriorArtPanelProps {
  projectId: string
  /** The invention's total document count, as shown on the page's Documents tab. */
  documentCount?: number
}

export default function ProjectPriorArtPanel({ projectId, documentCount }: ProjectPriorArtPanelProps) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [reprocessJob, setReprocessJob] = useState<ReprocessJob | null>(null)

  const { data: priorArt, isLoading } = useQuery({
    queryKey: ['project-prior-art', projectId],
    queryFn: () => getProjectPriorArt(projectId),
    enabled: !!projectId,
  })

  const reEvalMutation = useMutation({
    mutationFn: () => reEvaluateProjectPriorArt(projectId),
    onSuccess: (job) => setReprocessJob(job),
  })

  // Poll for re-evaluate job completion
  useEffect(() => {
    if (!reprocessJob || reprocessJob.status === 'completed' || reprocessJob.status === 'failed') return
    const interval = setInterval(async () => {
      try {
        const status = await getReprocessStatus(projectId, reprocessJob.job_id)
        setReprocessJob(status)
        if (status.status === 'completed') {
          queryClient.invalidateQueries({ queryKey: ['project-prior-art', projectId] })
          clearInterval(interval)
        }
        if (status.status === 'failed') clearInterval(interval)
      } catch {
        clearInterval(interval)
      }
    }, 3000)
    return () => clearInterval(interval)
  }, [reprocessJob, projectId, queryClient])

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
      </div>
    )
  }

  if (!priorArt) {
    return (
      <div className="text-center py-8">
        <Search className="w-10 h-10 mx-auto text-gray-300 mb-3" />
        <p className="text-sm text-gray-500">No prior art analysis yet. Generate AI Analysis first to enable prior art search.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Compact header: score + docs analysed + re-evaluate */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          {priorArt.overall_novelty_score != null
            ? <ScoreDisplay score={priorArt.overall_novelty_score} variant="novelty" size="lg" />
            : <span className="text-sm text-gray-500 italic">{priorArt.analysis_status === 'no_documents' ? 'No documents' : 'Analysis pending'}</span>
          }
          {priorArt.confidence_score != null && (
            <ScoreDisplay score={priorArt.confidence_score} variant="confidence" />
          )}
          <span className="text-xs text-gray-500">{analysedLabel(priorArt.total_documents_analysed, documentCount)}</span>
        </div>
        <button
          onClick={() => reEvalMutation.mutate()}
          disabled={reEvalMutation.isPending || (reprocessJob?.status === 'in_progress')}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          {(reEvalMutation.isPending || reprocessJob?.status === 'in_progress') ? (
            <><Loader2 className="w-3 h-3 animate-spin" /> Re-evaluating...</>
          ) : (
            <><RefreshCw className="w-3 h-3" /> Re-evaluate</>
          )}
        </button>
      </div>

      {/* Overlap Areas Table */}
      {priorArt.overlap_areas.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Overlap Areas</h4>
          <div className="border border-gray-100 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Overlap Area</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Severity</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Similarity</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Prior Art Reference</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Found In</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {priorArt.overlap_areas.map((area: any, i: number) => {
                  // Task #3326 r3 fix: the backend aggregator now propagates patent_id +
                  // patent_title for patent entries.  Render a clickable row + mono badge
                  // when patent_id is present (falls back to paper_id-looks-like-patent
                  // for legacy data).
                  const patentId: string = (area.patent_id || '').toString()
                  const paperIdRaw: string = (area.paper_id || '').toString()
                  const clickId = patentId || (/^[A-Z]{2}\d{4,12}[A-Z]?\d?$/.test(paperIdRaw) ? paperIdRaw : '')
                  const onClick = clickId ? () => navigate(`/patents/${encodeURIComponent(clickId)}`) : undefined
                  return (
                    <tr
                      key={i}
                      className={`hover:bg-gray-50 ${onClick ? 'cursor-pointer' : ''}`}
                      onClick={onClick}
                      title={onClick ? `Open patent ${clickId} detail` : undefined}
                    >
                      <td className="px-3 py-2 text-gray-900"><span>{linkPatentIds(area.area)}</span></td>
                      <td className="px-3 py-2">
                        {area.overlap_severity && (
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            area.overlap_severity === 'high'
                              ? 'bg-red-50 text-red-600'
                              : area.overlap_severity === 'medium'
                              ? 'bg-amber-50 text-amber-600'
                              : 'bg-green-50 text-green-600'
                          }`}>
                            {area.overlap_severity}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2"><ScoreDisplay score={area.similarity} variant="similarity" size="sm" /></td>
                      <td className="px-3 py-2 text-gray-700">
                        <div className="flex items-center gap-2">
                          <span>{area.source}</span>
                          {clickId && (
                            <span className="text-[10px] font-mono text-iflow-600 bg-iflow-50 px-1.5 py-0.5 rounded">
                              {clickId}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-gray-500 text-xs">{area.analyzed_document || ''}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Related Papers */}
      {priorArt.related_papers.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Prior Art References</h4>
          <div className="space-y-2">
            {priorArt.related_papers
              .sort((a, b) => b.similarity - a.similarity)
              .map((paper) => (
                <div key={paper.paper_id} className="flex items-start gap-3 p-2.5 border border-gray-100 rounded-lg hover:bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{paper.title}</p>
                    <p className="text-xs text-gray-500">
                      From {paper.source_documents.length} document{paper.source_documents.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <ScoreDisplay score={paper.similarity} variant="similarity" size="sm" />
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Patent Matches */}
      {priorArt.patent_matches.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Patent Matches</h4>
          <div className="space-y-2">
            {priorArt.patent_matches
              .sort((a, b) => b.similarity - a.similarity)
              .map((patent) => (
                <button
                  key={patent.patent_id}
                  type="button"
                  onClick={() => navigate(`/patents/${encodeURIComponent(patent.patent_id)}`)}
                  className="w-full text-left flex items-start gap-3 p-2.5 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors"
                  title={`Open patent ${patent.patent_id} detail`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{patent.title}</p>
                    <p className="text-xs text-gray-500 font-mono">{patent.patent_id}</p>
                  </div>
                  <ScoreDisplay score={patent.similarity} variant="similarity" size="sm" />
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  )
}
